<!--
  TopLayer
  ========
  Renders its slot content inside the browser's *native top layer*.

  Why this exists: Vivid's `VDialog` with the `modal` attribute renders a native
  `<dialog>` in its shadow root and opens it with `showModal()`. That promotes the
  dialog into the top layer, which paints above every normal-flow element no matter
  how large its `z-index` is. So the global loader and the global toast (both plain
  `position: fixed` overlays in App.vue) end up *underneath* any open Vivid modal.

  The only way out is for the overlay to enter the top layer too. This component
  does that with the Popover API: the host `<div popover="manual">` is promoted via
  `showPopover()`. Top layer paint order follows *entry* order, so the host
  deliberately leaves and re-enters the top layer every time it becomes visible —
  that guarantees it sits above any dialog that was opened earlier.

  `popover="manual"` (not `auto`) is used on purpose: a manual popover is never
  light-dismissed by Escape or an outside click, and showing it does not close
  unrelated popovers.
-->
<template>
  <div
    v-if="supported"
    ref="host"
    class="top-layer-host"
    popover="manual"
  >
    <slot></slot>
  </div>
  <slot v-else></slot>
</template>

<script>
export default {
  name: 'TopLayer',
  props: {
    // Whether the slot content is currently on screen.
    visible: {
      type: Boolean,
      default: false
    },
    // Keep the host in the top layer for this many ms after `visible` turns
    // false, so leave animations (e.g. Ionic's 300ms toast leave) can finish.
    hideDelay: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      // When the Popover API is missing we render the slot without a host so the
      // overlay keeps working exactly as it did before (below modals, but visible).
      supported: typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype.showPopover === 'function'
    };
  },
  watch: {
    visible(value) {
      value ? this.enterTopLayer() : this.scheduleLeave();
    }
  },
  mounted() {
    if (this.visible) this.enterTopLayer();
  },
  beforeUnmount() {
    this.clearTimer();
    this.leaveTopLayer();
  },
  methods: {
    enterTopLayer() {
      this.clearTimer();
      const host = this.$refs.host;
      if (!host) return;
      // Leave first so we re-enter the top layer *after* anything already in it
      // (such as a modal dialog opened before this overlay was triggered).
      this.leaveTopLayer();
      try {
        host.showPopover();
      } catch (error) {
        // Not connected yet / already open — harmless, the overlay still renders.
      }
    },
    leaveTopLayer() {
      const host = this.$refs.host;
      if (!host) return;
      try {
        if (host.matches(':popover-open')) host.hidePopover();
      } catch (error) {
        // Ignore: the host may already be detached.
      }
    },
    scheduleLeave() {
      this.clearTimer();
      if (!this.hideDelay) {
        this.leaveTopLayer();
        return;
      }
      this.timer = setTimeout(() => {
        this.timer = null;
        this.leaveTopLayer();
      }, this.hideDelay);
    },
    clearTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }
  }
};
</script>

<style scoped>
/*
  Reset the UA popover styles so the host is an invisible, full-viewport layer
  that simply carries its children into the top layer. It must not swallow
  pointer events — each child declares its own `pointer-events` (the loader
  backdrop sets `auto` to block interaction, Ionic's toast wrapper does the same
  for just the toast itself).
*/
.top-layer-host {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  overflow: visible;
  pointer-events: none;
}

/* A popover that is not showing must not take part in normal flow. */
.top-layer-host:not(:popover-open) {
  display: none;
}
</style>
